import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DevisStatut, IDevisResponse } from 'src/app/shared/models/devis';
import { DevisService } from '../../devis.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { OrderFormService } from 'src/app/core/orders-forms/services/order-form.service';
import { IOrderForm } from 'src/app/shared/models/order-form';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';

@Component({
  selector: 'app-devis-validate-dialog',
  templateUrl: './devis-validate-dialog.component.html',
  styleUrls: ['./devis-validate-dialog.component.less']
})
export class DevisValidateDialogComponent implements OnInit {

  form: FormGroup;
  saving = false;
  orderForms: IOrderForm[] = [];
  filteredOrders$!: Observable<IOrderForm[]>;

  constructor(
    private fb: FormBuilder,
    private devisService: DevisService,
    private orderFormService: OrderFormService,
    private snackbarService: SnackbarService,
    private authService: AuthenticationService,
    public dialogRef: MatDialogRef<DevisValidateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IDevisResponse
  ) {
    this.form = this.fb.group({
      devisDateFinTravaux:    [null, Validators.required],
      devisNumeroBonCommande: ['',   Validators.required]
    });
  }

  ngOnInit(): void {
    this.orderFormService.getOrdersForms().subscribe({
      next: orders => {
        this.orderForms = orders;
        this.filteredOrders$ = this.form.get('devisNumeroBonCommande')!.valueChanges.pipe(
          startWith(''),
          map(val => this.filterOrders(typeof val === 'string' ? val : val?.number ?? ''))
        );
      },
      error: () => { /* liste vide, saisie manuelle possible */ }
    });
  }

  filterOrders(term: string): IOrderForm[] {
    const t = term.toLowerCase();
    return this.orderForms.filter(o =>
      o.number?.toLowerCase().includes(t) ||
      o.description?.toLowerCase().includes(t) ||
      String(o.referenceYear).includes(t)
    );
  }

  displayOrder(order: IOrderForm | string | null): string {
    if (!order || typeof order === 'string') return order as string ?? '';
    return order.number;
  }

  selectOrder(order: IOrderForm): void {
    this.form.get('devisNumeroBonCommande')!.setValue(order.number, { emitEvent: true });
  }

  private toLocalDateStr(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  onValider(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const { devisDateFinTravaux, devisNumeroBonCommande } = this.form.value;
    const dateStr: string = devisDateFinTravaux instanceof Date
      ? this.toLocalDateStr(devisDateFinTravaux)
      : devisDateFinTravaux;
    const userRole = this.authService.getUser()?.profile as string ?? undefined;

    // Un seul PATCH : statut + numeroBdc + dateFinTravaux
    this.devisService.updateStatut(
      this.data.devisId,
      DevisStatut.ACCEPTE,
      undefined,
      devisNumeroBonCommande ?? undefined,
      dateStr ?? undefined,
      undefined,
      userRole
    ).subscribe({
      next: () => {
        this.snackbarService.openSnackBar('Devis validé avec succès');
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackbarService.openSnackBar('Erreur lors de la validation', 'error-snack-bar');
        this.saving = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
